import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiSeed, FiCheck, FiX, FiImage, FiDollarSign, FiSearch } = FiIcons;

const VarietyManager = ({ varieties: initialVarieties, onUpdate }) => {
  const [varieties, setVarieties] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVariety, setEditingVariety] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    growingDays: '',
    darkTreatmentDays: '',
    needPress: false,
    image: null,
    description: '',
    category: 'Microgreens',
    germinationMethod: '',
    seedQuantityGram: '',
    price: '',
    supplierPrice: '',
    size: '',
    media: '',
    needSoak: false,
    soakDuration: ''
  });
  const [activeTab, setActiveTab] = useState('Microgreens');
  const [searchQuery, setSearchQuery] = useState('');
  const categories = ['Microgreens', 'Edible Flowers', 'Edible Leaves', 'Lettuces'];
  const [mediaOptions, setMediaOptions] = useState(['Vermiculite', 'Cotton', 'Oasis']);
  const [isCustomMedia, setIsCustomMedia] = useState(false);

  // Fetch varieties on mount
  React.useEffect(() => {
    fetchVarieties();
    fetchMediaOptions();
  }, []);

  const fetchMediaOptions = async () => {
    try {
      const response = await fetch('/api/media-options');
      if (response.ok) {
        const data = await response.json();
        setMediaOptions(data);
      }
    } catch (error) {
      console.log('Failed to fetch media options, using defaults');
    }
  };

  const fetchVarieties = async () => {
    try {
      const response = await fetch('/api/varieties');
      if (response.ok) {
        const data = await response.json();
        setVarieties(data);
        if (onUpdate) onUpdate(); // Trigger parent refresh (for other tabs)
      }
    } catch (error) {
      console.error('Failed to fetch varieties:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const url = editingVariety
        ? `/api/varieties/${editingVariety.id}`
        : '/api/varieties';

      const method = editingVariety ? 'PUT' : 'POST';

      const data = new FormData();
      data.append('name', formData.name);
      data.append('growingDays', formData.growingDays);
      data.append('darkTreatmentDays', formData.darkTreatmentDays);
      data.append('needPress', formData.needPress);
      data.append('needSoak', formData.needSoak);
      data.append('soakDuration', formData.soakDuration);
      data.append('description', formData.description);
      data.append('category', formData.category);
      data.append('germinationMethod', formData.germinationMethod);
      data.append('seedQuantityGram', formData.seedQuantityGram);
      data.append('price', formData.price);
      data.append('supplierPrice', formData.supplierPrice);
      data.append('size', formData.size);
      data.append('media', formData.media);
      if (formData.image instanceof File) {
        data.append('image', formData.image);
      }

      const response = await fetch(url, {
        method,
        body: data,
      });

      if (response.ok) {
        await fetchVarieties();
        await fetchMediaOptions(); // Refresh list to include any new media
        resetForm();
      }
    } catch (error) {
      console.error('Failed to save variety:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this variety?')) return;

    try {
      const response = await fetch(`/api/varieties/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchVarieties();
      }
    } catch (error) {
      console.error('Failed to delete variety:', error);
    }
  };

  const resetForm = () => {
    setIsModalOpen(false);
    setEditingVariety(null);
    setFormData({ name: '', growingDays: '', darkTreatmentDays: '', needPress: false, needSoak: false, soakDuration: '', image: null, description: '', category: 'Microgreens', germinationMethod: '', seedQuantityGram: '', price: '', supplierPrice: '', size: '', media: '' });
    setIsCustomMedia(false);
  };

  const handleEdit = (variety) => {
    setEditingVariety(variety);
    setFormData({
      name: variety.name,
      growingDays: variety.growingDays.toString(),
      darkTreatmentDays: variety.darkTreatmentDays.toString(),
      needPress: variety.needPress || false,
      image: null,
      description: variety.description || '',
      category: variety.category || 'Microgreens',
      germinationMethod: variety.germinationMethod || '',
      seedQuantityGram: variety.seedQuantityGram ? Number(variety.seedQuantityGram).toFixed(1) : '',
      price: variety.price ? Number(variety.price).toFixed(2) : '',
      supplierPrice: variety.supplierPrice ? Number(variety.supplierPrice).toFixed(2) : '',
      size: variety.size || '',
      media: variety.media || '',
      needSoak: variety.needSoak || false,
      soakDuration: variety.soakDuration || ''
    });

    // Check if media is in our list, if not (and not empty), it might be custom or just not loaded yet.
    // However, since we fetch all distinct media, it SHOULD be in the list.
    // Unless it was just added.
    // Safe bet: reset custom to false, as the select will show it if it's in mediaOptions.
    setIsCustomMedia(false);

    setIsModalOpen(true);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({ ...prev, image: e.target.files[0] }));
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto pb-24 md:pb-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-10 gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] tracking-tight">Varieties</h1>
          <p className="text-gray-500 mt-2 font-light">Configure growth parameters and crop requirements</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full md:w-auto bg-[#16a34a] hover:bg-green-700 text-white px-6 py-3 rounded-lg flex items-center justify-center space-x-2 transition-all shadow-md shadow-green-100 font-medium"
        >
          <SafeIcon icon={FiPlus} />
          <span>New Variety</span>
        </button>
      </div>

      {/* Category Tabs & Search */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div className="flex space-x-2 bg-white p-1 rounded-xl w-full md:w-fit border border-gray-100 shadow-sm overflow-x-auto no-scrollbar">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveTab(cat)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${activeTab === cat
                ? 'bg-[#16a34a] text-white shadow-md shadow-green-100'
                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-64">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <SafeIcon icon={FiSearch} />
          </span>
          <input
            type="text"
            placeholder="Search varieties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] text-sm font-light shadow-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
        {varieties
          .filter(v => {
            const matchesCategory = activeTab === 'All' || (v.category || 'Microgreens') === activeTab;
            const matchesSearch = v.name.toLowerCase().includes(searchQuery.toLowerCase());
            return matchesCategory && matchesSearch;
          })
          .sort((a, b) => (a.name || '').trim().toLowerCase().localeCompare((b.name || '').trim().toLowerCase()))
          .map(variety => (
            <div key={variety.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 hover:shadow-lg transition-all duration-300">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start space-x-4">
                  <div className="w-24 h-24 bg-green-50 rounded-xl flex items-center justify-center text-[#16a34a] shadow-inner overflow-hidden flex-shrink-0 mt-1">
                    {variety.image ? (
                      <img src={variety.image} alt={variety.name} className="w-full h-full object-cover" />
                    ) : (
                      <SafeIcon icon={FiSeed} className="text-4xl" />
                    )}
                  </div>
                  <div className="flex flex-col">
                    <h3 className="text-xl font-semibold text-gray-900 leading-tight mb-2">{variety.name}</h3>
                    <div className="space-y-2">
                      {variety.price > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Standard</p>
                          <p className="text-sm font-semibold text-[#16a34a]">RM {Number(variety.price).toFixed(2)}</p>
                        </div>
                      )}
                      {variety.supplierPrice > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Supplier</p>
                          <p className="text-sm font-semibold text-[#1e3a8a]">RM {Number(variety.supplierPrice).toFixed(2)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button onClick={() => handleEdit(variety)} className="p-1.5 text-gray-400 hover:text-[#1e3a8a] hover:bg-blue-50 rounded-lg transition-all"><SafeIcon icon={FiEdit2} className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(variety.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><SafeIcon icon={FiTrash2} className="w-4 h-4" /></button>
                </div>
              </div>

              <div className="h-10 mb-6">
                <p className="text-sm text-gray-500 line-clamp-2">{variety.description || ''}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-3 rounded-xl">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Growing Phase</p>
                  <p className="text-lg font-light text-gray-900">{variety.growingDays} Days</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-xl">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                    {(variety.category === 'Microgreens' || !variety.category) ? 'Dark Phase' : 'Germination Phase'}
                  </p>
                  <p className="text-lg font-light text-gray-900">{variety.darkTreatmentDays} Days</p>
                </div>
                {/* Soak Info - Always Show */}
                <div className="bg-gray-50 p-3 rounded-xl">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Required Soak</p>
                  <p className="text-sm font-light text-gray-900">
                    {variety.needSoak ? `${variety.soakDuration} Days` : '-'}
                  </p>
                </div>
                {/* Press Info - Always Show */}
                <div className="bg-gray-50 p-3 rounded-xl">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Required Press</p>
                  <p className="text-sm font-light text-gray-900">
                    {variety.needPress ? `${variety.germinationMethod} Days` : '-'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-3 rounded-xl">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Media</p>
                  <p className="text-sm font-light text-gray-900 truncate" title={variety.media}>{variety.media || '-'}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-xl">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Seed Qty</p>
                  <p className="text-sm font-light text-gray-900">
                    {variety.seedQuantityGram ? (
                      ['Edible Flowers', 'Lettuces', 'Edible Leaves'].includes(variety.category)
                        ? `${Number(variety.seedQuantityGram)} pcs`
                        : `${Number(variety.seedQuantityGram).toFixed(1)}g`
                    ) : '-'}
                  </p>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-100 flex justify-between items-center">
                <span className="text-sm font-medium text-gray-500">Total Cycle</span>
                <span className="text-2xl font-light text-[#16a34a]">
                  {variety.growingDays + variety.darkTreatmentDays} Days
                </span>
              </div>
            </div>
          ))}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-[#1e3a8a]/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
              <h2 className="text-xl font-light text-[#1e3a8a]">{editingVariety ? 'Edit Variety' : 'New Variety'}</h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto">
              {/* Image Upload */}
              <div className="flex justify-center mb-4">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center border-2 border-dashed border-gray-300 relative overflow-hidden group hover:border-[#16a34a] transition-all">
                  {formData.image instanceof File ? (
                    <img src={URL.createObjectURL(formData.image)} alt="Preview" className="w-full h-full object-cover" />
                  ) : editingVariety && editingVariety.image ? (
                    <img src={editingVariety.image} alt="Current" className="w-full h-full object-cover" />
                  ) : (
                    <SafeIcon icon={FiImage} className="text-gray-400 text-2xl group-hover:text-[#16a34a]" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Variety Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                  placeholder="e.g. Radish Rambo"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(p => ({ ...p, category: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light bg-white"
                >
                  {categories.filter(c => c !== 'All').map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Standard Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3.5 text-gray-400 font-light text-sm">RM</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData(p => ({ ...p, price: e.target.value }))}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Supplier Price</label>
                  <div className="relative">
                    <span className="absolute left-4 top-3.5 text-gray-400 font-light text-sm">RM</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.supplierPrice}
                      onChange={(e) => setFormData(p => ({ ...p, supplierPrice: e.target.value }))}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light resize-none h-24"
                  placeholder="Describe the variety details..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {['Edible Flowers', 'Lettuces', 'Edible Leaves'].includes(formData.category) ? 'Seed Qty (pcs)' : 'Seed Qty (g)'}
                  </label>
                  <input
                    type="number"
                    step={['Edible Flowers', 'Lettuces', 'Edible Leaves'].includes(formData.category) ? "1" : "0.1"}
                    value={formData.seedQuantityGram}
                    onChange={(e) => setFormData(p => ({ ...p, seedQuantityGram: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                    placeholder={['Edible Flowers', 'Lettuces', 'Edible Leaves'].includes(formData.category) ? "0" : "0.0"}
                  />
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Media</label>
                    <div className="relative">
                      <select
                        value={formData.media}
                        onChange={(e) => setFormData(p => ({ ...p, media: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light bg-white"
                      >
                        <option value="">Select Media</option>
                        {mediaOptions.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {formData.category === 'Lettuces' || formData.category === 'Edible Flowers' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Germination Phase</label>
                      <input
                        type="number"
                        value={formData.darkTreatmentDays}
                        onChange={(e) => setFormData(p => ({ ...p, darkTreatmentDays: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                        min="0"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Growing Phase</label>
                      <input
                        type="number"
                        value={formData.growingDays}
                        onChange={(e) => setFormData(p => ({ ...p, growingDays: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                        min="1"
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Growing Phase</label>
                      <input
                        type="number"
                        value={formData.growingDays}
                        onChange={(e) => setFormData(p => ({ ...p, growingDays: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                        min="1"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {formData.category === 'Microgreens' ? 'Dark Phase' : 'Germination Phase'}
                      </label>
                      <input
                        type="number"
                        value={formData.darkTreatmentDays}
                        onChange={(e) => setFormData(p => ({ ...p, darkTreatmentDays: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                        min="0"
                        required
                      />
                    </div>
                  </>
                )}
              </div>


              <div className="grid grid-cols-2 gap-4">
                {/* Need Soak */}
                <div className="bg-gray-50 p-4 rounded-xl flex items-center justify-between gap-2">
                  <div className="flex flex-col flex-1">
                    <p className="text-sm font-medium text-gray-900">Need Soak?</p>
                    <p className="text-[10px] text-gray-500 font-medium">Soak seeds</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {formData.needSoak && (
                      <input
                        type="text"
                        placeholder="Days"
                        value={formData.soakDuration}
                        onChange={(e) => setFormData(p => ({ ...p, soakDuration: e.target.value }))}
                        className="w-16 px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-[#16a34a] text-center"
                      />
                    )}
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={formData.needSoak}
                        onChange={(e) => setFormData(p => ({ ...p, needSoak: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#16a34a]"></div>
                    </label>
                  </div>
                </div>

                {/* Need Press (Microgreen Only) */}
                {formData.category === 'Microgreens' && (
                  <div className="bg-gray-50 p-4 rounded-xl flex items-center justify-between gap-2">
                    <div className="flex flex-col flex-1">
                      <p className="text-sm font-medium text-gray-900">Need Press?</p>
                      <p className="text-[10px] text-gray-500 font-medium">Apply Weight</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {formData.needPress && (
                        <input
                          type="text"
                          placeholder="Days"
                          value={formData.germinationMethod}
                          onChange={(e) => setFormData(p => ({ ...p, germinationMethod: e.target.value }))}
                          className="w-16 px-2 py-1.5 text-xs border border-gray-200 rounded-lg outline-none focus:border-[#16a34a] text-center"
                        />
                      )}
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={formData.needPress}
                          onChange={(e) => setFormData(p => ({ ...p, needPress: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#16a34a]"></div>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-green-50 rounded-xl p-4 flex justify-between items-center border border-green-100">
                <div>
                  <p className="text-sm font-medium text-green-800">Total Production Cycle</p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-[#16a34a]">
                    {((parseInt(formData.growingDays) || 0) +
                      (parseInt(formData.darkTreatmentDays) || 0) +
                      (formData.needSoak ? (parseFloat(formData.soakDuration) || 0) : 0) +
                      ((formData.needPress && formData.category === 'Microgreens') ? (parseFloat(formData.germinationMethod) || 0) : 0)).toFixed(1).replace(/\.0$/, '')}
                  </span>
                  <span className="text-sm font-medium text-green-700 ml-1">Days</span>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="submit" disabled={isLoading} className="flex-1 bg-[#16a34a] text-white py-4 rounded-lg font-medium hover:bg-green-700 transition-all shadow-lg shadow-green-100 disabled:opacity-50">
                  {isLoading ? 'Saving...' : 'Save Variety'}
                </button>
                <button type="button" onClick={resetForm} className="px-6 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all">
                  Cancel
                </button>
              </div>
            </form>
          </div >
        </div >
      )}
    </div >
  );
};


export default VarietyManager;